import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Helmet from 'components/Helmet';
import Loader from 'components/Loader';
import Button from 'components/Button';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import TestFinderModule from './Module';
import './index.css';

type TransitionType = 'sat' | 'act' | null;

const TEST_FINDER_TEST_ID = 'test-finder-sat-vs-act';

function TestFinderTest() {
    const [isLoading, setIsLoading] = useState(true);
    const [studentTestId, setStudentTestId] = useState<string | null>(null);
    const [transition, setTransition] = useState<TransitionType>('sat');
    const [testName, setTestName] = useState('');
    const [moduleId, setModuleId] = useState('');
    const [moduleName, setModuleName] = useState('');
    const [moduleNumber, setModuleNumber] = useState(0);
    const [moduleSubject, setModuleSubject] = useState('');
    const [modulePrompt, setModulePrompt] = useState('');
    const [breakAfter, setBreakAfter] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [moduleDuration, setModuleDuration] = useState<number | null>(null);
    const [moduleTime, setModuleTime] = useState<number | null>(null);
    const [questionTime, setQuestionTime] = useState<number | null>(null);

    const { user } = useAppSelector(state => state.user);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const studentTestIdRef = useRef<string | null>(null);

    // Update the ref whenever studentTestId changes
    useEffect(() => {
        studentTestIdRef.current = studentTestId;
    }, [studentTestId]);

    // First, get or create the studentTestId
    const initializeTest = () => {
        const isFreshStart = (location.state as any)?.freshStart === true;
        
        axios.get('testfinder/')
            .then(res => {
                
                if (res.data.hasCompletedTest) {
                    // Test already completed, go to results
                    navigate('/test-finder');
                    return;
                }
                
                if (res.data.hasExistingAttempt && res.data.existingAttemptId) {
                    // Resume existing test or continue fresh start
                    setStudentTestId(res.data.existingAttemptId);
                    getNextModule(res.data.existingAttemptId, isFreshStart);
                } else {
                    // Need to create a new test - redirect to test-finder landing
                    // which will handle the POST to create the test
                    navigate('/test-finder');
                }
            })
            .catch(error => {
                dispatch(setError(error));
            });
    };

    const getNextModule = (testId: string, skipIntro: boolean = false) => {
        setIsLoading(true);
        setModuleDuration(null);
        axios.get(`testfinder/${testId}/`)
            .then(res => {
                if (res.data.id) {
                    setTestName(res.data.testName);
                    setModuleId(res.data.id);
                    setModuleName(res.data.name);
                    setModuleNumber(res.data.number);
                    setModuleSubject(res.data.subject);
                    setModuleDuration(res.data.duration);
                    setModuleTime(res.data.duration);
                    setModulePrompt(res.data.prompt);
                    setBreakAfter(res.data.breakAfter);
                    setQuestions(res.data.questions);
                    setAnswers(res.data.answers);
                    setQuestionIndex(res.data.lastQuestionIndex || 0);
                    setQuestionTime(0);

                    // Only show intro screens on initial load, not when navigating between modules
                    if (skipIntro) {
                        setTransition(null);
                    } else {
                        // Determine if we should show SAT intro
                        // Show SAT intro if:
                        // 1. This is the first module (isFirstModule from backend) OR module name indicates SAT Module 1
                        // 2. AND no answers have been saved for this module yet
                        const hasNoAnswers = !res.data.answers.some((a: any) => a.answer);
                        const isFirstModule = res.data.isFirstModule === true;
                        const isSatModuleOne = res.data.name?.includes('SAT') && res.data.number === 1;
                        
                        // Show SAT intro on the very first module of the test
                        if (hasNoAnswers && (isFirstModule || isSatModuleOne)) {
                            setTransition('sat');
                        } else {
                            setTransition(null);
                        }
                    }

                    // Check for autosave
                    if (sessionStorage.getItem('testfinder-autosave')) {
                        const autosave = JSON.parse(sessionStorage.getItem('testfinder-autosave'));
                        if (autosave.moduleId === res.data.id) {
                            setAnswers(autosave.answers);
                            setModuleDuration(autosave.moduleTime);
                        }
                    }

                    setIsLoading(false);
                } else {
                    // Test completed, redirect to results
                    navigate('/test-finder');
                }
            })
            .catch(error => {
                dispatch(setError(error));
            });
    };

    const sendReport = () => {
        axios.post('debug/', {
            email: user.email,
            message: JSON.stringify({
                module: `${testName} ${moduleName}`,
                answers: answers,
                moduleDuration: moduleDuration,
                moduleTime: moduleTime
            })
        });
    };

    const autoSave = () => {
        sessionStorage.setItem('testfinder-autosave', JSON.stringify({
            moduleId: moduleId,
            answers: answers,
            moduleTime: moduleTime
        }));
    };

    const navigateQuestion = (index: number) => {
        if (questionIndex >= 0 && questionIndex < questions.length) {
            let newAnswers = [...answers];
            newAnswers[questionIndex].timeSpent += questionTime;
            setAnswers(newAnswers);
        }
        setQuestionTime(0);
        setQuestionIndex(index);
        autoSave();
    };

    const setAnswer = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answer;
        setAnswers(newAnswers);
        autoSave();
    };

    const setChoice = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answers[questionIndex].answer === answer ? '' : answer;
        newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== answer);
        setAnswers(newAnswers);
        autoSave();
    };

    const markQuestion = () => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].marked = !answers[questionIndex].marked;
        setAnswers(newAnswers);
        autoSave();
    };

    const crossOutChoice = (choiceId: string) => {
        let newAnswers = [...answers];
        if (newAnswers[questionIndex].crossedOut.includes(choiceId)) {
            newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== choiceId);
        } else {
            newAnswers[questionIndex].crossedOut.push(choiceId);
        }
        if (choiceId === answers[questionIndex].answer) {
            newAnswers[questionIndex].answer = '';
        }
        setAnswers(newAnswers);
        autoSave();
    };

    const saveAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations[index] = annotation;
        } else {
            const coveredAnnotations = answers[questionIndex].annotations.filter(existing => (
                existing.passageId === annotation.passageId &&
                existing.startIndex >= annotation.startIndex &&
                existing.endIndex <= annotation.endIndex
            ));
            newAnswers[questionIndex].annotations.push(annotation);
            for (let i = 0; i < coveredAnnotations.length; i++) {
                const coveredIndex = newAnswers[questionIndex].annotations.findIndex(existing => (
                    existing.passageId === coveredAnnotations[i].passageId &&
                    existing.startIndex === coveredAnnotations[i].startIndex &&
                    existing.endIndex === coveredAnnotations[i].endIndex
                ));
                const coveredAnnotation = newAnswers[questionIndex].annotations.splice(coveredIndex, 1)[0];
                newAnswers[questionIndex].annotations.push(coveredAnnotation);
            }
        }
        setAnswers(newAnswers);
        autoSave();
    };

    const deleteAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations.splice(index, 1);
        }
        setAnswers(newAnswers);
        autoSave();
    };

    const handleSubmit = () => {
        const currentStudentTestId = studentTestIdRef.current;
        if (!currentStudentTestId) return;

        let newAnswers = [...answers];
        if (questionIndex >= 0 && questionIndex < questions.length) {
            newAnswers[questionIndex].timeSpent += questionTime;
        }
        setIsLoading(true);
        sendReport();
        
        // Stop timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
        
        axios.post(`testfinder/submit/${currentStudentTestId}/${moduleId}/`, {
            answers: newAnswers,
            timeLeft: moduleTime
        })
        .then(() => {
            setModuleTime(null);
            setQuestionTime(null);
            setIsLoading(false);
            
            if (breakAfter) {
                setBreakAfter(false);
                // Show ACT transition
                setTransition('act');
            } else {
                // Skip intro when moving to next module
                getNextModule(currentStudentTestId, true);
            }
        })
        .catch(error => {
            dispatch(setError(error));
        });
    };

    const handleSaveAndExit = () => {
        if (!studentTestId) return;

        setIsLoading(true);
        
        // Stop timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
        
        axios.post(`testfinder/save/${studentTestId}/${moduleId}/`, {
            answers: answers,
            timeLeft: moduleTime
        })
        .then(() => {
            navigate('/sat-or-act');
        })
        .catch(error => {
            dispatch(setError(error));
        });
    };

    const handleDiscardAndExit = () => {
        sessionStorage.removeItem('testfinder-autosave');
        
        // Stop timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
        
        if (moduleId && studentTestId) {
            axios.post(`testfinder/discard/${studentTestId}/${moduleId}/`)
                .then(() => {
                    navigate('/sat-or-act');
                })
                .catch(() => {
                    navigate('/sat-or-act');
                });
        } else {
            navigate('/sat-or-act');
        }
    };

    const handleTransitionComplete = () => {
        setTransition(null);
        if (transition === 'act' && studentTestId) {
            // Skip intro when transitioning from ACT intro screen
            getNextModule(studentTestId, true);
        }
    };

    // Initialize test on mount
    useEffect(() => {
        initializeTest();
        
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, []);

    // Set module time when instruction ends
    useEffect(() => {
        if (!transition && moduleDuration !== null) {
            setModuleTime(moduleDuration);
        }
    }, [transition, moduleDuration]);

    // Timer effect
    useEffect(() => {
        if (isLoading || transition) return;
        if (moduleTime === null || questionTime === null) return;
        if (timerIntervalRef.current) return;

        timerIntervalRef.current = setInterval(() => {
            setModuleTime(prev => {
                if (prev <= 1) {
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
            setQuestionTime(prev => prev + 1);
        }, 1000);

        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, [isLoading, transition, moduleTime !== null, questionTime !== null]);

    if (isLoading) {
        return <Loader />;
    }

    // Transition screen (SAT or ACT intro)
    if (transition) {
        return (
            <div className='testFinderTest' onCopy={(event) => { event.preventDefault() }}>
                <Helmet title='SAT vs ACT Test Finder - The SAT Crash Course' />
                <div className='testFinderTest__box testFinderTest__box--transition'>
                    <div className='testFinderTest__transition'>
                        <h1 className='testFinderTest__transitionTitle'>
                            {transition === 'sat' 
                                ? 'SAT Portion'
                                : 'ACT Portion'
                            }
                        </h1>
                        <p className='testFinderTest__transitionText'>
                            {transition === 'sat' 
                                ? 'You will now complete questions in the SAT format. This portion includes a 8 Minute long Reading & Writing section and a 9 minute long Math section.'
                                : 'You will now complete questions in the ACT format. This portion includes the Reading & Writing and Math sections, both 7 minutes long.'
                              }
                        </p>
                        <Button
                            className='testFinderTest__transitionButton'
                            onClick={handleTransitionComplete}
                        >
                            Start Now
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='testFinderTest' onCopy={(event) => { event.preventDefault() }}>
            <Helmet title='SAT vs ACT Test Finder - The SAT Crash Course' />
            <div className='testFinderTest__box'>
                <TestFinderModule
                    testId={TEST_FINDER_TEST_ID}
                    moduleName={moduleName}
                    subject={moduleSubject}
                    timeLeft={moduleTime}
                    isUntimed={false}
                    disableAnnotations={moduleName?.includes('ACT')}
                    discardButtonText="DISCARD MODULE"
                    prompt={modulePrompt}
                    questions={questions}
                    questionIndex={questionIndex}
                    answers={answers}
                    markQuestion={markQuestion}
                    crossOutChoice={crossOutChoice}
                    saveAnnotation={saveAnnotation}
                    deleteAnnotation={deleteAnnotation}
                    handleChange={value => setAnswer(value)}
                    handleSelect={value => setChoice(value)}
                    navigateQuestion={navigateQuestion}
                    handleSaveAndExit={handleSaveAndExit}
                    handleDiscardAndExit={handleDiscardAndExit}
                    handleSubmit={handleSubmit}
                />
            </div>
        </div>
    );
}

export default TestFinderTest;
